import React from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";

import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";

import { mergeDeepRight, pathOr, isNil, reject, mergeLeft } from "ramda";
import { PersistGate } from "redux-persist/es/integration/react";

import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// import { ThemeProvider } from "styled-components";

import { ThemeProvider as MuiThemeProvider, createTheme, StyledEngineProvider } from "@mui/material/styles";

import "~/libs/print";

import EclipseLoader from "~/components/EclipseLoader";

import configureStore from "~/store/configure";
import mildenbergerTheme from "~/themes/mildenberger";
import App from "~/App";
import { replicateUiState, replicateDlsMaterials, replicateMaterialPool } from "~/libs/pouch";
import { initializeNes } from "~/libs/nes";
import domainReducer from "~/store/ducks/domain";
import { setDLSLoading } from "~/store/ducks/application/materials/isDLSMaterialsLoading";
import { showModal } from "~/store/ducks/domain/ui/modal";

import { browserHistory, getQueryValues } from "~/store/enhancers/query-sync";

const muiCache = createCache({
  key: "mui",
  prepend: true
});

const initialQuery = new URLSearchParams(window.location.search);

const configuration = window.getClientConfiguration();

const isDls = pathOr(false, ["book", "settings", "features", "dls"], configuration);

const domain = {
  config: {
    drm: pathOr(false, ["book", "drm"], configuration),
    offset: pathOr(0, ["book", "pageOffset"], configuration),
    pagesCount: pathOr(0, ["book", "pagesCount"], configuration),
    catsort: pathOr({}, ["book", "catsort"], configuration),
    outline: pathOr([], ["book", "outline"], configuration),
    title: pathOr("", ["book", "pdfinfo", "Title"], configuration),
    printablePages: pathOr([], ["book", "printablePages"], configuration),
    size: pathOr({}, ["book", "size"], configuration),
    features: pathOr({}, ["book", "settings", "features"], configuration),
    pageControl: pathOr([], ["book", "pageControl"], configuration),
    usersettings: pathOr([], ["book", "usersettings"], configuration),
    nonPrintableMessageUseDefault: pathOr(true, ["book", "nonPrintableMessage", "useDefault"], configuration),
    nonPrintableMessageAdditionalInfo: pathOr("", ["book", "nonPrintableMessage", "additionalInfo"], configuration)
  },
  ui: pathOr({}, ["book", "ui"], configuration)
};

const { store, persistor } = configureStore({
  domain: mergeDeepRight(domainReducer(), domain)
});

const theme = createTheme(mildenbergerTheme);

const onBeforeLift = () => {
  const { config } = store.getState().domain;

  return Promise.resolve(config.drm)
    .then(isDrm => {
      if (isDrm) {
        return replicateUiState(store);
      }

      return Promise.resolve();
    })
    .then(() => {
      const currentQuery = new URLSearchParams(window.location.search);

      const initialValues = reject(isNil, getQueryValues(initialQuery));
      const currentValues = reject(isNil, getQueryValues(currentQuery));

      const mergedValues = mergeLeft(initialValues, currentValues);
      const hydratedQuery = new URLSearchParams();

      Object.entries(mergedValues).forEach(([param, value]) => {
        hydratedQuery.append(param, value);
      });

      browserHistory.replace({
        search: `?${hydratedQuery.toString()}`
      });
    });
};

const onApplicationStart = () => {
  // console.log({ isDls });

  initializeNes(configuration, store);
  Promise.resolve()
    .then(() => (isDls ? replicateDlsMaterials(store) : Promise.resolve()))
    .then(() => (isDls ? replicateMaterialPool(store) : Promise.resolve()))
    .catch(err => {
      if (err.name === "indexed_db_went_bad") {
        store.dispatch(showModal("POUCH_ERROR", {}));
      }

      console.error("error");
      console.error(err);
    })
    .finally(() => store.dispatch(setDLSLoading(false)));
};

const onBootstrap = isDone =>
  isDone ? <App onApplicationStart={onApplicationStart} /> : <EclipseLoader color="gray" />;

const root = document.getElementById("app");

const renderApp = () =>
  render(
    <Provider store={store}>
      {/* <StyledEngineProvider injectFirst> */}
      <CacheProvider value={muiCache}>
        {/* <ThemeProvider theme={theme}> */}
        <MuiThemeProvider theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <PersistGate persistor={persistor} onBeforeLift={onBeforeLift}>
              {onBootstrap}
            </PersistGate>
          </DndProvider>
        </MuiThemeProvider>
        {/* </ThemeProvider> */}
      </CacheProvider>
      {/* </StyledEngineProvider> */}
    </Provider>,
    root
  );

renderApp();

// if (module.hot) {
//   module.hot.accept("~/App", renderApp);
// }

export {};
